import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  DollarSign,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import {
  Button,
  EmptyState,
  Field,
  FinancialStatusBadge,
  FilterPill,
  Input,
  Modal,
  PageHeader,
  Select,
  StatCard,
  TBody,
  TD,
  TH,
  THead,
  TR,
  ToastContainer,
  useToast,
} from '../ui';
import { financialsService } from '../../services/financials.service';
import { customersService } from '../../services/customers.service';
import CustomerPicker from './CustomerPicker';
import CustomerHoverCard from '../shared/CustomerHoverCard';
import { avatarInitials } from '../customers/CustomersList';
import useCustomerName from '../../hooks/useCustomerName';

/* ── Avatar color (same palette as CustomersList / CustomerHoverCard) ─────── */
const AVATAR_COLORS = [
  'bg-accent-100 text-accent-700',
  'bg-success-100 text-success-700',
  'bg-info-100 text-info-700',
  'bg-warning-100 text-warning-800',
  'bg-danger-100 text-danger-700',
];
const avatarColor = (id) => AVATAR_COLORS[(id?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

/* ── Customer cell with resolved name ────────────────────────────────────── */
const CustomerCell = ({ customerId }) => {
  const { name, loading } = useCustomerName(customerId);
  const initials = avatarInitials(
    name !== customerId ? name.split(' ')[0] : undefined,
    name !== customerId ? name.split(' ').slice(1).join(' ') || undefined : undefined,
    undefined,
  );
  const color = avatarColor(customerId);
  const shortId = customerId ? `${customerId.slice(0, 12)}${customerId.length > 12 ? '…' : ''}` : '—';

  return (
    <div className="flex items-center gap-2 min-w-0">
      <div
        className={`h-7 w-7 rounded-full flex items-center justify-center text-2xs font-bold shrink-0 ${color}`}
        aria-hidden="true"
      >
        {loading ? '·' : initials}
      </div>
      <div className="min-w-0">
        {loading ? (
          <div className="w-24 h-4 bg-gray-200 rounded animate-pulse" data-testid="customer-name-skeleton" />
        ) : (
          <p className="text-xs font-medium text-ink-800 truncate leading-tight">
            {name !== customerId ? name : <span className="font-mono text-ink-500">{shortId}</span>}
          </p>
        )}
        <p className="text-2xs font-mono text-ink-400 truncate leading-tight mt-0.5">{shortId}</p>
      </div>
    </div>
  );
};

/* ── Exported helper (tests depend on this export) ────────────────────────── */
export const maskIban = (iban) => {
  const clean = (iban || '').replace(/\s+/g, '');
  if (clean.length <= 8) return clean;
  return `${clean.slice(0, 4)} •••• •••• ${clean.slice(-4)}`;
};

/* ── Helpers ──────────────────────────────────────────────────────────────── */
const fmtRelative = (v) => {
  if (!v) return '—';
  const diff = Math.floor((Date.now() - new Date(v)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(v).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
};

const fmtAbsolute = (v) =>
  v
    ? new Date(v).toLocaleString('en-GB', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const fmtAmount = (amount) => {
  if (amount == null) return '—';
  const n = parseFloat(amount);
  if (isNaN(n)) return String(amount);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* ── Filter config ────────────────────────────────────────────────────────── */
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'CREATED', label: 'Created' },
  { value: 'PENDING_APPROVAL', label: 'Pending review' },
  { value: 'RECEIVED', label: 'Received' },
  { value: 'UNDER_PROCESSING', label: 'Processing' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'PENDING_PAYMENT_ACTION', label: 'Awaiting payment' },
];

const QUICK_FILTERS = [
  { label: 'Pending', value: 'PENDING_APPROVAL', tone: 'warning' },
  { label: 'Approved', value: 'APPROVED', tone: 'success' },
  { label: 'Rejected', value: 'REJECTED', tone: 'danger' },
  { label: 'Cancelled', value: 'CANCELLED', tone: 'neutral' },
];

const TYPE_TABS = [
  { value: '', label: 'All' },
  { value: 'DEPOSIT', label: 'Deposits' },
  { value: 'WITHDRAWAL', label: 'Withdrawals' },
];

/* ── Row skeleton ─────────────────────────────────────────────────────────── */
const RowSkeleton = () => (
  <TR>
    <TD>
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-full bg-ink-100 animate-pulse shrink-0" />
        <div className="h-3 w-16 bg-ink-100 animate-pulse rounded" />
      </div>
    </TD>
    <TD><div className="h-3 w-28 bg-ink-100 animate-pulse rounded" /></TD>
    <TD><div className="h-3 w-20 bg-ink-100 animate-pulse rounded ml-auto" /></TD>
    <TD><div className="h-5 w-24 bg-ink-100 animate-pulse rounded" /></TD>
    <TD><div className="h-3 w-16 bg-ink-100 animate-pulse rounded" /></TD>
    <TD><div className="h-3 w-24 bg-ink-100 animate-pulse rounded" /></TD>
    <TD />
  </TR>
);

/* ── Withdrawal modal helpers ─────────────────────────────────────────────── */
const SectionHead = ({ children }) => (
  <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400 mb-3 mt-1">{children}</p>
);

const LinkedBankAccountPicker = ({ accounts, selectedId, onSelect, onUseManualEntry }) => (
  <div className="space-y-2">
    {accounts.map((account) => (
      <label
        key={account.bankAccountId}
        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
          selectedId === account.bankAccountId
            ? 'bg-accent-50 border-accent-300'
            : 'bg-white border-ink-200 hover:border-accent-200'
        }`}
      >
        <input
          type="radio"
          name="linkedBankAccount"
          value={account.bankAccountId}
          checked={selectedId === account.bankAccountId}
          onChange={() => onSelect(account.bankAccountId)}
          className="mt-0.5 accent-accent-600"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <CreditCard className="h-3.5 w-3.5 text-ink-400 shrink-0" />
            <span className="text-sm font-medium text-ink-900 truncate">{account.beneficiaryName}</span>
            <span className="text-xs text-ink-400 shrink-0">{account.currency}</span>
          </div>
          <p className="text-xs text-ink-500 mt-0.5 font-mono">{maskIban(account.iban)}</p>
          {account.bankName && <p className="text-xs text-ink-400 mt-0.5 truncate">{account.bankName}</p>}
        </div>
      </label>
    ))}
    <button type="button" onClick={onUseManualEntry} className="text-xs text-accent-600 hover:text-accent-700 font-medium">
      Use a different bank account
    </button>
  </div>
);

/* ── Create Deposit Modal ─────────────────────────────────────────────────── */
const DEPOSIT_EMPTY_FORM = { customerId: '', amount: '', currency: 'AED', notes: '' };

const CreateDepositModal = ({ open, onClose, onSuccess, showToast }) => {
  const [form, setForm] = useState(DEPOSIT_EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleClose = () => {
    setForm(DEPOSIT_EMPTY_FORM);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await financialsService.createDeposit({
        customerId: form.customerId.trim(),
        amount: form.amount,
        currency: form.currency,
        notes: form.notes || undefined,
      });
      showToast(
        `${result.autoApproved ? 'Deposit created & auto-approved' : 'Deposit submitted for review'} — reference ${result.referenceCode}`,
        'success',
      );
      onSuccess?.();
      handleClose();
    } catch (err) {
      showToast(err?.response?.data?.message || err.message || 'Failed to create deposit.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New deposit"
      width="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={loading} disabled={!form.customerId.trim() || !form.amount}>
            Create deposit
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Customer" required>
          <CustomerPicker value={form.customerId} onChange={(customerId) => set('customerId', customerId)} autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Amount" required>
            <Input mono value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="1000.00" required pattern="^\d+(\.\d+)?$" />
          </Field>
          <Field label="Currency">
            <Select value={form.currency} onChange={(e) => set('currency', e.target.value)}>
              {['AED', 'USD', 'EUR', 'GBP', 'SAR'].map((c) => <option key={c}>{c}</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Notes">
          <Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Optional" />
        </Field>
      </form>
    </Modal>
  );
};

/* ── Create Withdrawal Modal ─────────────────────────────────────────────── */
const WITHDRAWAL_EMPTY_FORM = {
  customerId: '', amount: '', currency: 'AED', notes: '',
  bankAccountId: '',
  iban: '', beneficiaryName: '', bankName: '', swiftCode: '', bankCurrency: 'AED',
};

const CreateWithdrawalModal = ({ open, onClose, onSuccess, showToast }) => {
  const [form, setForm] = useState(WITHDRAWAL_EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [linkedBankAccounts, setLinkedBankAccounts] = useState([]);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
  const [useManualEntry, setUseManualEntry] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!form.customerId) {
      setLinkedBankAccounts([]);
      setUseManualEntry(false);
      return;
    }
    let cancelled = false;
    setBankAccountsLoading(true);
    customersService
      .listBankAccounts(form.customerId)
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        setLinkedBankAccounts(items);
        setUseManualEntry(items.length === 0);
        setForm((f) => ({ ...f, bankAccountId: items[0]?.bankAccountId || '' }));
      })
      .catch(() => {
        if (cancelled) return;
        setLinkedBankAccounts([]);
        setUseManualEntry(true);
      })
      .finally(() => {
        if (!cancelled) setBankAccountsLoading(false);
      });
    return () => { cancelled = true; };
  }, [form.customerId]);

  const handleClose = () => {
    setForm(WITHDRAWAL_EMPTY_FORM);
    setLinkedBankAccounts([]);
    setUseManualEntry(false);
    onClose();
  };

  const hasLinkedAccountSelected = linkedBankAccounts.length > 0 && !useManualEntry;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        customerId: form.customerId.trim(),
        amount: form.amount,
        currency: form.currency,
        notes: form.notes || undefined,
      };
      if (hasLinkedAccountSelected) {
        payload.bankAccountId = form.bankAccountId;
      } else {
        payload.userBankAccount = {
          iban: form.iban,
          beneficiaryName: form.beneficiaryName,
          bankName: form.bankName,
          swiftCode: form.swiftCode,
          currency: form.bankCurrency,
        };
      }
      const result = await financialsService.createWithdrawal(payload);
      showToast(`Withdrawal submitted for review — reference ${result.referenceCode}`, 'success');
      onSuccess?.();
      handleClose();
    } catch (err) {
      showToast(err?.response?.data?.message || err.message || 'Failed to create withdrawal.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = form.customerId.trim() && form.amount && (
    hasLinkedAccountSelected
      ? Boolean(form.bankAccountId)
      : (form.iban && form.beneficiaryName && form.bankName && form.swiftCode)
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New withdrawal"
      width="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={loading} disabled={!canSubmit}>
            Create withdrawal
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Customer" required>
          <CustomerPicker value={form.customerId} onChange={(customerId) => set('customerId', customerId)} autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Amount" required>
            <Input mono value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="500.00" required pattern="^\d+(\.\d+)?$" />
          </Field>
          <Field label="Currency">
            <Select value={form.currency} onChange={(e) => set('currency', e.target.value)}>
              {['AED', 'USD', 'EUR', 'GBP', 'SAR'].map((c) => <option key={c}>{c}</option>)}
            </Select>
          </Field>
        </div>

        <SectionHead>Bank account</SectionHead>
        {bankAccountsLoading ? (
          <div className="flex items-center gap-2 text-sm text-ink-400 py-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading linked bank accounts…
          </div>
        ) : hasLinkedAccountSelected ? (
          <LinkedBankAccountPicker
            accounts={linkedBankAccounts}
            selectedId={form.bankAccountId}
            onSelect={(id) => set('bankAccountId', id)}
            onUseManualEntry={() => setUseManualEntry(true)}
          />
        ) : (
          <>
            {linkedBankAccounts.length > 0 && (
              <button
                type="button"
                onClick={() => setUseManualEntry(false)}
                className="mb-2 text-xs text-accent-600 hover:text-accent-700 font-medium"
              >
                Use a saved bank account instead
              </button>
            )}
            <Field label="IBAN" required>
              <Input mono value={form.iban} onChange={(e) => set('iban', e.target.value)} placeholder="AE07 0000 0000 0000 0000 000" required />
            </Field>
            <Field label="Beneficiary name" required>
              <Input value={form.beneficiaryName} onChange={(e) => set('beneficiaryName', e.target.value)} placeholder="Full name on the account" required />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Bank name" required>
                <Input value={form.bankName} onChange={(e) => set('bankName', e.target.value)} placeholder="Bank name" required />
              </Field>
              <Field label="SWIFT / BIC" required>
                <Input mono value={form.swiftCode} onChange={(e) => set('swiftCode', e.target.value)} placeholder="XXXXXXXX" required />
              </Field>
            </div>
            <Field label="Bank currency">
              <Select value={form.bankCurrency} onChange={(e) => set('bankCurrency', e.target.value)}>
                {['AED', 'USD', 'EUR', 'GBP', 'SAR'].map((c) => <option key={c}>{c}</option>)}
              </Select>
            </Field>
          </>
        )}

        <Field label="Notes">
          <Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Optional" />
        </Field>
      </form>
    </Modal>
  );
};

/* ── Main component ───────────────────────────────────────────────────────── */
const FinancialsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toasts, show: showToast, dismiss } = useToast();

  /* filters — `customerId` supports deep links (e.g. from the customer detail
     page's "Cash in/out" quick action); the search box doubles as the
     customer-ID filter, so seed it from either param. */
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [search, setSearch] = useState(
    searchParams.get('search') || searchParams.get('customerId') || '',
  );
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const statsDebounceRef = useRef(null);
  const requestIdRef = useRef(0);

  /* data */
  const [deposits, setDeposits] = useState([]);
  const [depositsTotal, setDepositsTotal] = useState(0);
  const [depositsNextCursor, setDepositsNextCursor] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalsTotal, setWithdrawalsTotal] = useState(0);
  const [withdrawalsNextCursor, setWithdrawalsNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* modals */
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);

  /* true aggregate counts for the stat cards — fetched independently of the
     status filter and pagination (limit: 1, we only need `total`), so they
     always reflect all matching transactions rather than just the loaded
     page or whatever status the user currently has selected. */
  const [pendingTotal, setPendingTotal] = useState(0);
  const [approvedTotal, setApprovedTotal] = useState(0);

  /* sync URL */
  useEffect(() => {
    const p = {};
    if (typeFilter) p.type = typeFilter;
    if (statusFilter) p.status = statusFilter;
    if (search) p.search = search;
    setSearchParams(p, { replace: true });
  }, [typeFilter, statusFilter, search, setSearchParams]);

  /* click-outside for status dropdown */
  useEffect(() => {
    const handler = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setShowStatusDropdown(false);
      }
    };
    if (showStatusDropdown) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showStatusDropdown]);

  /* fetch both types in parallel — always, to power stats for all items.
     Guarded by a request ID so a slow, superseded request (e.g. a filter
     changed again before the first response arrived) can never clobber
     state with stale results. */
  const load = useCallback(async (dCursor = null, wCursor = null) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 50 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.customerId = search;

      const [depsResult, wthsResult] = await Promise.allSettled([
        financialsService.listDeposits({ ...params, ...(dCursor ? { cursor: dCursor } : {}) }),
        financialsService.listWithdrawals({ ...params, ...(wCursor ? { cursor: wCursor } : {}) }),
      ]);

      if (requestId !== requestIdRef.current) return; // superseded by a newer request

      if (depsResult.status === 'fulfilled' && depsResult.value) {
        const r = depsResult.value;
        setDeposits((prev) => (dCursor ? [...prev, ...(r.items || [])] : (r.items || [])));
        setDepositsTotal(r.total || 0);
        setDepositsNextCursor(r.nextCursor || null);
      }
      if (wthsResult.status === 'fulfilled' && wthsResult.value) {
        const r = wthsResult.value;
        setWithdrawals((prev) => (wCursor ? [...prev, ...(r.items || [])] : (r.items || [])));
        setWithdrawalsTotal(r.total || 0);
        setWithdrawalsNextCursor(r.nextCursor || null);
      }
      if (depsResult.status === 'rejected' && wthsResult.status === 'rejected') {
        const msg = depsResult.reason?.response?.data?.message
          || depsResult.reason?.message
          || 'Failed to load financial data.';
        setError(msg);
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [statusFilter, search]);

  /* reload with debounce on search */
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!search) {
      setDeposits([]);
      setWithdrawals([]);
      load(null, null);
    } else {
      debounceRef.current = setTimeout(() => {
        setDeposits([]);
        setWithdrawals([]);
        load(null, null);
      }, 350);
    }
    return () => clearTimeout(debounceRef.current);
  }, [search, statusFilter, load]);

  /* load true aggregate pending/approved counts, independent of statusFilter */
  const loadStats = useCallback(async () => {
    const params = { limit: 1 };
    if (search) params.customerId = search;
    try {
      const results = await Promise.allSettled([
        financialsService.listDeposits({ ...params, status: 'PENDING_APPROVAL' }),
        financialsService.listWithdrawals({ ...params, status: 'PENDING_APPROVAL' }),
        financialsService.listDeposits({ ...params, status: 'CREATED' }),
        financialsService.listWithdrawals({ ...params, status: 'CREATED' }),
        financialsService.listDeposits({ ...params, status: 'APPROVED' }),
        financialsService.listWithdrawals({ ...params, status: 'APPROVED' }),
      ]);
      const total = (r) => (r.status === 'fulfilled' ? r.value?.total || 0 : 0);
      const [depPending, wthPending, depCreated, wthCreated, depApproved, wthApproved] = results;
      setPendingTotal(total(depPending) + total(wthPending) + total(depCreated) + total(wthCreated));
      setApprovedTotal(total(depApproved) + total(wthApproved));
    } catch {
      // Stat cards are a non-critical overview; leave last-known values on failure.
    }
  }, [search]);

  useEffect(() => {
    clearTimeout(statsDebounceRef.current);
    statsDebounceRef.current = setTimeout(loadStats, search ? 350 : 0);
    return () => clearTimeout(statsDebounceRef.current);
  }, [loadStats, search]);

  /* merged sorted list */
  const allItems = useMemo(() => {
    const deps = deposits.map((d) => ({ ...d, _type: 'DEPOSIT', _id: d.intentId || d.id }));
    const wths = withdrawals.map((w) => ({ ...w, _type: 'WITHDRAWAL', _id: w.intentId || w.id }));
    return [...deps, ...wths].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [deposits, withdrawals]);

  /* stat cards */
  const stats = useMemo(() => ({
    totalDeposits: depositsTotal,
    totalWithdrawals: withdrawalsTotal,
    pending: pendingTotal,
    approved: approvedTotal,
  }), [depositsTotal, withdrawalsTotal, pendingTotal, approvedTotal]);

  /* table rows — filtered by typeFilter client-side */
  const tableItems = useMemo(() => {
    if (!typeFilter) return allItems;
    return allItems.filter((item) => item._type === typeFilter);
  }, [allItems, typeFilter]);

  const hasMore = Boolean(
    (depositsNextCursor && typeFilter !== 'WITHDRAWAL') ||
    (withdrawalsNextCursor && typeFilter !== 'DEPOSIT'),
  );

  const handleLoadMore = () => {
    load(
      typeFilter !== 'WITHDRAWAL' ? depositsNextCursor : null,
      typeFilter !== 'DEPOSIT' ? withdrawalsNextCursor : null,
    );
  };

  const handleRefresh = () => {
    setDeposits([]);
    setWithdrawals([]);
    load(null, null);
    loadStats();
  };

  const handleRowClick = (item) => {
    navigate(`/financials/${item._id}?type=${item._type}`);
  };

  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setTypeFilter('');
  };

  const activeFilterCount = (search ? 1 : 0) + (statusFilter ? 1 : 0);

  return (
    <div>
      <ToastContainer toasts={toasts} onDismiss={dismiss} />

      <PageHeader
        title="Cash In/Out"
        description="Deposits and withdrawals across all customers."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" iconLeft={Plus} onClick={() => setShowWithdrawalModal(true)}>
              New withdrawal
            </Button>
            <Button variant="primary" iconLeft={Plus} onClick={() => setShowDepositModal(true)}>
              New deposit
            </Button>
          </div>
        }
      />

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Total Deposits"
          value={stats.totalDeposits.toLocaleString('en-US')}
          icon={ArrowDownLeft}
        />
        <StatCard
          label="Total Withdrawals"
          value={stats.totalWithdrawals.toLocaleString('en-US')}
          icon={ArrowUpRight}
        />
        <StatCard
          label="Pending"
          value={stats.pending.toLocaleString('en-US')}
          icon={DollarSign}
        />
        <StatCard
          label="Completed"
          value={stats.approved.toLocaleString('en-US')}
          icon={CheckCircle2}
        />
      </div>

      {/* Type filter tabs */}
      <div className="flex border-b border-ink-200 mb-4">
        {TYPE_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setTypeFilter(tab.value)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px
              ${typeFilter === tab.value
                ? 'border-accent-600 text-accent-700'
                : 'border-transparent text-ink-500 hover:text-ink-800 hover:border-ink-300'
              }`}
          >
            {tab.label}
            {tab.value === '' && allItems.length > 0 && (
              <span className="text-xs text-ink-400 tnum">({allItems.length})</span>
            )}
            {tab.value === 'DEPOSIT' && depositsTotal > 0 && (
              <span className="text-xs text-ink-400 tnum">({depositsTotal})</span>
            )}
            {tab.value === 'WITHDRAWAL' && withdrawalsTotal > 0 && (
              <span className="text-xs text-ink-400 tnum">({withdrawalsTotal})</span>
            )}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by customer ID…"
            className="input pl-8 text-sm h-9 w-full"
            data-testid="search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Status filter dropdown */}
        <div className="relative" ref={statusDropdownRef}>
          <button
            type="button"
            onClick={() => setShowStatusDropdown((o) => !o)}
            className={`btn btn-sm btn-secondary gap-1.5 ${statusFilter ? 'border-accent-300 text-accent-700 bg-accent-50' : ''}`}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            Status
            {statusFilter && (
              <span className="ml-0.5 h-4 w-4 rounded-full bg-accent-600 text-white text-2xs flex items-center justify-center font-bold">
                1
              </span>
            )}
          </button>
          {showStatusDropdown && (
            <div className="absolute top-full mt-1 left-0 z-20 w-52 bg-white rounded-xl shadow-elevated border border-ink-200 py-1">
              <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400 px-3 pt-2 pb-1">
                Transaction status
              </p>
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setStatusFilter(opt.value); setShowStatusDropdown(false); }}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between transition-colors
                    ${statusFilter === opt.value
                      ? 'bg-accent-50 text-accent-700 font-medium'
                      : 'text-ink-700 hover:bg-ink-50'
                    }`}
                >
                  {opt.label}
                  {statusFilter === opt.value && (
                    <span className="h-1.5 w-1.5 rounded-full bg-accent-600" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Clear active filters */}
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="text-xs text-ink-500 hover:text-ink-800 flex items-center gap-1"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}

        {/* Quick filter pills */}
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          {QUICK_FILTERS.map((qf) => (
            <button
              key={qf.value}
              type="button"
              onClick={() => setStatusFilter((s) => (s === qf.value ? '' : qf.value))}
              className={`badge cursor-pointer transition-colors text-xs
                ${statusFilter === qf.value
                  ? qf.tone === 'success' ? 'bg-success-100 text-success-800 border-success-200'
                    : qf.tone === 'warning' ? 'bg-warning-100 text-warning-800 border-warning-200'
                    : qf.tone === 'danger' ? 'bg-danger-100 text-danger-800 border-danger-200'
                    : 'bg-ink-100 text-ink-800 border-ink-200'
                  : 'bg-ink-50 text-ink-500 border-ink-200 hover:bg-ink-100'
                }`}
            >
              {qf.label}
            </button>
          ))}
        </div>

        {/* Refresh */}
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          className="btn btn-sm btn-secondary"
          title="Refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Active filter pills */}
      {(search || statusFilter) && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {search && (
            <FilterPill label="Search" value={search} onRemove={() => setSearch('')} />
          )}
          {statusFilter && (
            <FilterPill
              label="Status"
              value={STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label || statusFilter}
              onRemove={() => setStatusFilter('')}
            />
          )}
        </div>
      )}

      {/* Table card */}
      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        {error ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-sm text-danger-600">{error}</p>
            <button type="button" onClick={handleRefresh} className="btn btn-sm btn-secondary">
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table w-full">
                <THead>
                  <TR>
                    <TH className="w-36">Type</TH>
                    <TH className="w-44">Customer</TH>
                    <TH className="w-36 text-right">Amount</TH>
                    <TH className="w-36">Status</TH>
                    <TH className="w-28">Date</TH>
                    <TH className="w-36">Reference</TH>
                    <TH className="w-6" />
                  </TR>
                </THead>
                <TBody>
                  {loading && tableItems.length === 0
                    ? Array.from({ length: 6 }).map((_, i) => <RowSkeleton key={i} />)
                    : tableItems.map((item) => (
                        <TR
                          key={`${item._type}-${item._id}`}
                          className="cursor-pointer hover:bg-accent-50/40 transition-colors"
                          onClick={() => handleRowClick(item)}
                          data-testid={`financial-row-${item._id}`}
                        >
                          <TD>
                            <div className="flex items-center gap-2">
                              {item._type === 'DEPOSIT' ? (
                                <div className="h-7 w-7 rounded-full bg-success-100 flex items-center justify-center shrink-0">
                                  <ArrowDownLeft className="h-3.5 w-3.5 text-success-700" />
                                </div>
                              ) : (
                                <div className="h-7 w-7 rounded-full bg-danger-100 flex items-center justify-center shrink-0">
                                  <ArrowUpRight className="h-3.5 w-3.5 text-danger-700" />
                                </div>
                              )}
                              <span className="text-xs font-medium text-ink-700">
                                {item._type === 'DEPOSIT' ? 'Deposit' : 'Withdrawal'}
                              </span>
                            </div>
                          </TD>
                          <TD>
                            <CustomerHoverCard customerId={item.customerId}>
                              <CustomerCell customerId={item.customerId} />
                            </CustomerHoverCard>
                          </TD>
                          <TD className="text-right">
                            <div className="flex items-baseline gap-1 justify-end">
                              <span
                                className={`text-sm font-semibold font-mono tnum ${
                                  item._type === 'DEPOSIT' ? 'text-success-700' : 'text-danger-700'
                                }`}
                              >
                                {item._type === 'DEPOSIT' ? '+' : '−'}{fmtAmount(item.amount)}
                              </span>
                              <span className="text-xs text-ink-400">{item.currency}</span>
                            </div>
                          </TD>
                          <TD>
                            <FinancialStatusBadge status={item.status} />
                          </TD>
                          <TD>
                            <time
                              title={fmtAbsolute(item.createdAt)}
                              className="text-xs text-ink-500 tnum"
                            >
                              {fmtRelative(item.createdAt)}
                            </time>
                          </TD>
                          <TD>
                            <span className="text-xs font-mono text-ink-500">
                              {item.referenceCode || '—'}
                            </span>
                          </TD>
                          <TD>
                            <ChevronRight className="h-4 w-4 text-ink-300" />
                          </TD>
                        </TR>
                      ))}
                </TBody>
              </table>
            </div>

            {/* Empty state */}
            {!loading && tableItems.length === 0 && (
              <EmptyState
                icon={DollarSign}
                title={
                  search || statusFilter || typeFilter
                    ? 'No transactions match your filters'
                    : 'No transactions yet'
                }
                description={
                  search || statusFilter || typeFilter
                    ? 'Try adjusting your search or filters.'
                    : 'Deposit and withdrawal intents will appear here once customers transact.'
                }
                action={
                  activeFilterCount > 0 || typeFilter ? (
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="btn btn-sm btn-secondary"
                    >
                      Clear filters
                    </button>
                  ) : undefined
                }
              />
            )}

            {/* Load more */}
            {hasMore && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-ink-100">
                <p className="text-xs text-ink-400 tnum">
                  Showing {tableItems.length} transaction{tableItems.length !== 1 ? 's' : ''}
                </p>
                <button
                  type="button"
                  disabled={loading}
                  onClick={handleLoadMore}
                  className="btn btn-sm btn-secondary"
                >
                  {loading ? (
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-current border-r-transparent animate-spin" />
                  ) : (
                    <DollarSign className="h-3.5 w-3.5" />
                  )}
                  Load more
                </button>
              </div>
            )}

            {/* Footer when all loaded */}
            {!hasMore && tableItems.length > 0 && (
              <div className="px-4 py-3 border-t border-ink-100 flex items-center justify-between">
                <p className="text-xs text-ink-400 tnum">
                  {tableItems.length} transaction{tableItems.length !== 1 ? 's' : ''}
                  {search || statusFilter || typeFilter ? ' matching filters' : ' total'}
                </p>
                {(activeFilterCount > 0 || typeFilter) && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="text-xs text-accent-600 hover:text-accent-800 font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <CreateDepositModal
        open={showDepositModal}
        onClose={() => setShowDepositModal(false)}
        onSuccess={handleRefresh}
        showToast={showToast}
      />
      <CreateWithdrawalModal
        open={showWithdrawalModal}
        onClose={() => setShowWithdrawalModal(false)}
        onSuccess={handleRefresh}
        showToast={showToast}
      />
    </div>
  );
};

export default FinancialsList;
